import {
  Button,
  BookmarkButton,
  CollapsibleTagList,
  LoaderScreen,
  ExternalLink,
} from '@/components/Elements';
import { Dialog } from '@/components/Dialog';
import {
  MentionTag,
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';
import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import * as QRCode from 'qrcode';
import {
  BuildingOffice2Icon,
  CheckIcon,
  ChevronLeftIcon,
  LinkIcon,
  GlobeAltIcon,
  CubeIcon,
  DocumentTextIcon,
  HashtagIcon,
  QrCodeIcon,
  XMarkIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getSafeExternalUrl } from '@/utils/url';
import { format } from '@formkit/tempo';
import clsx from 'clsx';
import { useClipboardCopy } from '@/hooks/useClipboardCopy';

const articleDetailContentFragment = graphql(`
  fragment ArticleDetailContentFragment on ActiveArticle {
    id
    code
    title
    url
    summary
    publishedAt
    country
    organizationType
    organization
    themes
    people
    companies
    locations
    displayableTopicTags {
      lookupKey
      name
      description
      themes
      isSocialIssue
    }
    isBookmarkedByCurrentUser
  }
`);

const sourcePathMap = {
  search: '/analyze/search',
  treemap: '/analyze/treemap',
  transition: '/analyze/transition',
  bookmark: '/bookmark/list',
  qr: '/analyze/search',
} as const;

const generateQrCode = async (sourceUrl: string): Promise<string> => {
  try {
    const qrSourceUrl = new URL(sourceUrl);
    qrSourceUrl.searchParams.set('from', 'qr_code');
    return await QRCode.toDataURL(qrSourceUrl.toString(), {
      width: 640,
      errorCorrectionLevel: 'M',
      margin: 2,
    });
  } catch {
    return '';
  }
};

type SourceType = keyof typeof sourcePathMap;

export const ArticleDetailContent = ({
  article,
}: {
  article: FragmentOf<typeof articleDetailContentFragment>;
}) => {
  const { t } = useTranslation();
  const item = readFragment(articleDetailContentFragment, article);
  const navigate = useNavigate();
  const safeItemUrl = getSafeExternalUrl(item.url);
  const [backPath, setBackPath] = useState('/analyze/search');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const { isCopied: isLinkCopied, copy: copyToClipboard } = useClipboardCopy();

  const hasMentionEntities =
    (item.people?.length ?? 0) > 0 ||
    (item.companies?.length ?? 0) > 0 ||
    (item.locations?.length ?? 0) > 0;
  const hasThemes = (item.themes?.length ?? 0) > 0;
  const hasTags = (item.displayableTopicTags?.length ?? 0) > 0;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const from = searchParams.get('from');
    const safeSource =
      from && Object.prototype.hasOwnProperty.call(sourcePathMap, from)
        ? (from as SourceType)
        : null;
    setBackPath(safeSource ? sourcePathMap[safeSource] : '/analyze/search');
    setCanGoBack(window.history.length > 1);
  }, []);

  const openQr = async () => {
    if (isQrLoading || qrCodeUrl) {
      setIsQrOpen(true);
      return;
    }

    setIsQrOpen(true);
    setIsQrLoading(true);
    try {
      const image = await generateQrCode(window.location.href);
      setQrCodeUrl(image);
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleBack = () => {
    if (canGoBack) {
      navigate(-1);
      return;
    }

    navigate(backPath);
  };

  const handleCopyLink = () => {
    void copyToClipboard(window.location.href);
  };

  const articleActionButtons = (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <button
        type="button"
        onClick={handleCopyLink}
        aria-label={t(
          isLinkCopied ? '記事のリンクをコピーしました' : '記事リンクをコピー'
        )}
        title={t(isLinkCopied ? 'コピーしました' : '記事リンクをコピー')}
        className={clsx(
          'grid size-7 place-items-center rounded-full bg-rw-white p-0 transition-colors hover:bg-rw-white/80',
          isLinkCopied ? 'text-emerald-600' : 'text-rw-sub'
        )}
      >
        {isLinkCopied ? (
          <CheckIcon className="size-4 stroke-2" />
        ) : (
          <LinkIcon className="size-4 stroke-2" />
        )}
        <span className="sr-only">
          {t(
            isLinkCopied ? '記事のリンクをコピーしました' : '記事リンクをコピー'
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={openQr}
        aria-label={t('QRコードを開く')}
        title={t('QRコードを開く')}
        className="grid size-7 place-items-center rounded-full bg-rw-white p-0 text-rw-sub transition-colors hover:bg-rw-white/80"
      >
        <QrCodeIcon className="size-4 stroke-2" />
        <span className="sr-only">{t('QRコードを開く')}</span>
      </button>
      <BookmarkButton
        code={item.code}
        title={item.title}
        url={item.url}
        isBookmarked={item.isBookmarkedByCurrentUser}
      />
    </div>
  );

  return (
    <div>
      {/* Navigation */}
      <Button
        type="button"
        color="transparent"
        size="sm"
        className="hidden w-fit pl-0 text-gray-600 hover:text-gray-800 sm:inline-flex"
        onClick={handleBack}
        startIcon={<ChevronLeftIcon className="size-4" />}
      >
        {t('戻る')}
      </Button>

      <article className="rounded-2xl bg-white p-4 shadow-sm sm:p-6 md:p-10">
        <Dialog
          isOpen={isQrOpen}
          onClose={() => setIsQrOpen(false)}
          maxWidth="sm"
          showCloseButton={false}
        >
          <div className="relative p-1">
            <Button
              type="button"
              color="transparent"
              size="sm"
              className="absolute right-0 top-0 -m-4 size-7 rounded-full p-0 text-gray-500 shadow-sm"
              onClick={() => setIsQrOpen(false)}
              aria-label={t('QRコードを閉じる')}
              title={t('QRコードを閉じる')}
              startIcon={<XMarkIcon className="size-5" />}
            >
              <span className="sr-only">{t('QRコードを閉じる')}</span>
            </Button>
            <div className="mx-auto mt-1 w-full">
              <div className="relative mx-auto aspect-square w-[260px] max-w-full">
                {isQrLoading && (
                  <div className="absolute inset-0">
                    <LoaderScreen contained />
                  </div>
                )}
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt={t('記事詳細のQRコード')}
                    className="size-full rounded-md border border-gray-200 object-contain"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-xs text-gray-500">
                    {t('QRコードを生成できませんでした')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog>

        <section className="space-y-4 sm:space-y-5">
          <header className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
              <h1 className="min-w-0 text-lg font-bold leading-tight tracking-tight sm:text-2xl md:text-3xl">
                {safeItemUrl ? (
                  <ExternalLink
                    to={safeItemUrl}
                    className="underline hover:no-underline"
                  >
                    {item.title}
                  </ExternalLink>
                ) : (
                  item.title
                )}
              </h1>
              <div className="shrink-0">{articleActionButtons}</div>
            </div>
          </header>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 sm:text-sm">
              <time
                dateTime={item.publishedAt}
                className="font-medium text-gray-700"
              >
                {format(item.publishedAt, { date: 'short' })}
              </time>
              {(item.country || item.organizationType || item.organization) && (
                <span className="text-gray-300">|</span>
              )}
              {item.country && (
                <MetadataTag icon={GlobeAltIcon} text={item.country} />
              )}
              {item.organizationType && (
                <MetadataTag icon={CubeIcon} text={item.organizationType} />
              )}
              {item.organization && (
                <MetadataTag
                  icon={BuildingOffice2Icon}
                  text={item.organization}
                />
              )}
            </div>

            {(hasThemes || hasTags) && (
              <div className="space-y-2 rounded-lg p-2.5 sm:p-3">
                {hasThemes && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="pt-1 text-xs font-medium text-gray-600 sm:text-sm">
                      {t('テーマ：')}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      <CollapsibleTagList
                        items={item.themes ?? []}
                        maxVisibleItems={3}
                        textColor="text-rw-sub"
                        renderTag={(theme) => (
                          <ThemeTag
                            key={theme}
                            icon={HashtagIcon}
                            tableName={theme}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
                {hasTags && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="pt-1 text-xs font-medium text-gray-600 sm:text-sm">
                      {t('タグ：')}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      <CollapsibleTagList
                        items={item.displayableTopicTags ?? []}
                        maxVisibleItems={6}
                        textColor="text-rw-sub"
                        renderTag={(tag) => (
                          <TopicTagWithTooltip
                            key={tag.lookupKey}
                            name={tag.name}
                            description={tag.description}
                            themes={tag.themes}
                            isSocialIssue={tag.isSocialIssue}
                            icon={TagIcon}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
          <section className="pt-2 sm:pt-3">
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-800">
              <DocumentTextIcon className="size-4 text-rw-sub" />
              {t('要約')}
            </h2>
            <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700 sm:text-base sm:leading-8">
              {item.summary ?? 'サマリがありません'}
            </p>
          </section>

          {hasMentionEntities && (
            <div className="space-y-6 pt-4 sm:pt-5">
              {(item.people?.length ?? 0) > 0 && (
                <TagContainer title={t('言及された人物')}>
                  {item.people!.map((person) => (
                    <MentionTag key={person} text={person} />
                  ))}
                </TagContainer>
              )}
              {(item.companies?.length ?? 0) > 0 && (
                <TagContainer title={t('言及された組織')}>
                  {item.companies!.map((company) => (
                    <MentionTag key={company} text={company} />
                  ))}
                </TagContainer>
              )}
              {(item.locations?.length ?? 0) > 0 && (
                <TagContainer title={t('言及された地名')}>
                  {item.locations!.map((location) => (
                    <MentionTag key={location} text={location} />
                  ))}
                </TagContainer>
              )}
            </div>
          )}
        </section>
      </article>
    </div>
  );
};

ArticleDetailContent.fragments = {
  data: articleDetailContentFragment,
};
